// src/components/BuildingViews/FurnaceView.js
import React, { useState } from 'react';
import styles from './FurnaceView.module.css';
import { ItemData } from '../../data/ItemData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const FurnaceView = ({ playerData, onPlayerAction, onGoBack }) => {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [globalFuelSelection, setGlobalFuelSelection] = useState('charcoal'); // Global fuel preference
  
  // Get current crafting status
  const activeCrafting = playerData?.buildings?.furnace?.activeCrafting;

  const furnaceRecipes = [
    {
      id: 'smeltCopper',
      name: 'Smelt Copper Bar',
      description: 'Transform copper ore into useful copper bars',
      input: { 'copperOre': 1 },
      fuel: { 'wood': 1, 'charcoal': 1 }, // Either wood or charcoal
      output: { 'copperBar': 1 },
      duration: 120, // 2 minutes (base duration)
      icon: 'crafting/copperBar.png'
    },
    {
      id: 'smeltIron',
      name: 'Smelt Iron Bar',
      description: 'Transform iron ore into sturdy iron bars',
      input: { 'ironOre': 1 },
      fuel: { 'wood': 2, 'charcoal': 1 }, // Iron needs more heat
      output: { 'ironBar': 1 },
      duration: 180, // 3 minutes (base duration)
      icon: 'crafting/ironBar.png'
    },
    {
      id: 'makeCharcoal',
      name: 'Make Charcoal',
      description: 'Burn wood to create efficient charcoal fuel',
      input: { 'wood': 2 },
      // No fuel required - this is the process of making fuel
      output: { 'charcoal': 1 },
      duration: 90, // 1.5 minutes
      icon: 'crafting/charcoal.png'
    }
  ];



  // Helper function to get available fuels for a recipe
  const getAvailableFuels = (recipe) => {
    if (!recipe.fuel) return [];
    
    return Object.keys(recipe.fuel).filter(fuelType => {
      const required = recipe.fuel[fuelType];
      const available = playerData.inventory[fuelType] || 0;
      return available >= required;
    });
  };

  // Helper function to get selected or best available fuel
  const getSelectedFuel = (recipe) => {
    if (!recipe.fuel) return null;
    
    const availableFuels = getAvailableFuels(recipe);
    
    // Use global fuel selection if it's available for this recipe
    if (globalFuelSelection && availableFuels.includes(globalFuelSelection)) {
      return globalFuelSelection;
    }
    
    // Otherwise, default to best available (prefer charcoal)
    if (availableFuels.includes('charcoal')) return 'charcoal';
    if (availableFuels.includes('wood')) return 'wood';
    return null;
  };

  const canCraftRecipe = (recipe) => {
    // Check input materials
    for (const [itemKey, requiredAmount] of Object.entries(recipe.input)) {
      const available = playerData.inventory[itemKey] || 0;
      if (available < requiredAmount) {
        return false;
      }
    }
    
    // Check fuel requirements
    if (recipe.fuel) {
      const selectedFuel = getSelectedFuel(recipe);
      if (!selectedFuel) {
        return false;
      }
    }
    
    return true;
  };

  const handleCraftClick = (recipe) => {
    if (!canCraftRecipe(recipe) || activeCrafting) {
      return;
    }

    setIsProcessing(true);
    setSelectedRecipe(recipe);
    
    // Use player's selected fuel
    const selectedFuel = getSelectedFuel(recipe);
    
    // Call the action through the parent component with fuel info
    onPlayerAction(recipe.id, { selectedFuel });
    
    // Reset processing state after a brief delay
    setTimeout(() => {
      setIsProcessing(false);
      setSelectedRecipe(null);
    }, 1000);
  };

  const handleFuelSelection = (fuelType) => {
    setGlobalFuelSelection(fuelType);
  };

  const handleStopCrafting = () => {
    onPlayerAction('stopCrafting', { buildingKey: 'furnace' });
  };

  const handleToggleContinuous = () => {
    onPlayerAction('toggleContinuousCrafting', { buildingKey: 'furnace' });
  };

  const buildingLevel = playerData.buildings?.furnace?.upgradeLevel || 0;
  const efficiencyBonus = buildingLevel * 10; // 10% time reduction per level

  return (
    <div className={styles.furnaceContainer}>
      {onGoBack && (
        <button 
          className={styles.backButton}
          onClick={onGoBack}
          title="Back to Home"
        >
          ✕
        </button>
      )}
      <div className={styles.furnaceHeader}>
        <div className={styles.furnaceMain}>
          <img src="/img/buildingItems/furnace.png" alt="Furnace" className={styles.furnaceIcon} />
          <div className={styles.furnaceInfo}>
            <h2>Furnace Operations</h2>
            <p>Level {buildingLevel} {efficiencyBonus > 0 && `(-${efficiencyBonus}% time)`}</p>
            <p className={styles.description}>
              Transform raw materials through the power of fire and heat
            </p>
          </div>
        </div>
        
        {/* Global Fuel Selection */}
        <div className={styles.globalFuelSelection}>
          <h3>🔥 Fuel Type</h3>
          <div className={styles.fuelToggle}>
            {['wood', 'charcoal'].map(fuelType => {
              const item = ItemData[fuelType];
              const available = playerData.inventory[fuelType] || 0;
              const isSelected = globalFuelSelection === fuelType;
              const isOptimal = fuelType === 'charcoal';
              const hasAny = available > 0;
              
              return (
                <div 
                  key={fuelType}
                  className={`${styles.globalFuelOption} ${isSelected ? styles.selected : ''} ${isOptimal ? styles.optimal : ''} ${!hasAny ? styles.insufficient : ''}`}
                  onClick={() => hasAny && handleFuelSelection(fuelType)}
                >
                  <input 
                    type="radio" 
                    name="global-fuel"
                    checked={isSelected}
                    onChange={() => handleFuelSelection(fuelType)}
                    disabled={!hasAny}
                  />
                  <img 
                    src={getItemImageSrc(fuelType)} 
                    alt={item?.name || fuelType} 
                    className={styles.fuelIcon} 
                  />
                  <div className={styles.globalFuelInfo}>
                    <span className={styles.globalFuelName}>{item?.name || fuelType}</span>
                    <span className={styles.globalFuelCount}>{available} available</span>
                    {isOptimal && <span className={styles.globalFuelBonus}>⚡ -50% time</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active Crafting Progress */}
      {activeCrafting && (
        <div className={styles.craftingProgress}>
          <h3>🔥 Currently Smelting</h3>
          <div className={styles.activeRecipe}>
            <div className={styles.recipeInfo}>
              <span className={styles.recipeName}>
                {activeCrafting.actionConfig.output ? 
                  Object.keys(activeCrafting.actionConfig.output).map(key => 
                    ItemData[key]?.name || key
                  ).join(', ') :
                  ItemData[activeCrafting.actionConfig.outputItemKey]?.name || activeCrafting.actionConfig.outputItemKey
                }
              </span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${(activeCrafting.progress || 0) * 100}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>
                {Math.floor((activeCrafting.progress || 0) * 100)}% Complete
              </span>
            </div>
            <div className={styles.craftingControls}>
              <button 
                onClick={handleToggleContinuous}
                className={`${styles.controlButton} ${activeCrafting.continuousCrafting ? styles.active : ''}`}
              >
                {activeCrafting.continuousCrafting ? '🔄 Continuous ON' : '🔄 Continuous OFF'}
              </button>
              <button 
                onClick={handleStopCrafting}
                className={`${styles.controlButton} ${styles.stopButton}`}
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.recipeGrid}>
        {furnaceRecipes.map((recipe) => {
          const canCraft = canCraftRecipe(recipe);
          const isSelected = selectedRecipe?.id === recipe.id;
          
          return (
            <div 
              key={recipe.id} 
              className={`${styles.recipeCard} ${!canCraft || activeCrafting ? styles.disabled : ''} ${isSelected ? styles.selected : ''}`}
            >
              <div className={styles.recipeHeader}>
                <img 
                  src={getItemImageSrc(recipe.icon)} 
                  alt={recipe.name} 
                  className={styles.recipeIcon} 
                />
                <div className={styles.recipeInfo}>
                  <h3>{recipe.name}</h3>
                  <p>{recipe.description}</p>
                </div>
              </div>

              <div className={styles.recipeDetails}>
                <div className={styles.requirements}>
                  <h4>Required:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.input).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      const available = playerData.inventory[itemKey] || 0;
                      const hasEnough = available >= amount;
                      
                      return (
                        <div key={itemKey} className={`${styles.itemRequirement} ${!hasEnough ? styles.insufficient : ''}`}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>
                            {available}/{amount}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Fuel Requirements Display */}
                {recipe.fuel && (
                  <div className={styles.fuelRequirements}>
                    <h4>Fuel Required:</h4>
                    <div className={styles.fuelDisplay}>
                      {(() => {
                        const selectedFuel = getSelectedFuel(recipe);
                        const requiredAmount = selectedFuel ? recipe.fuel[selectedFuel] : 0;
                        const available = playerData.inventory[selectedFuel] || 0;
                        const hasEnough = available >= requiredAmount;
                        const item = ItemData[selectedFuel];
                        
                        if (!selectedFuel) {
                          return <span className={styles.noFuelAvailable}>No fuel available!</span>;
                        }
                        
                        return (
                          <div className={`${styles.fuelDisplayItem} ${!hasEnough ? styles.insufficient : ''}`}>
                            <img 
                              src={getItemImageSrc(selectedFuel)} 
                              alt={item?.name || selectedFuel} 
                              className={styles.itemIcon} 
                            />
                            <span>{item?.name || selectedFuel}</span>
                            <span className={styles.itemAmount}>
                              {available}/{requiredAmount}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className={styles.output}>
                  <h4>Produces:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.output).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      
                      return (
                        <div key={itemKey} className={styles.itemOutput}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>x{amount}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.recipeStats}>
                  <div className={styles.duration}>
                    <span>⏱️ {Math.floor(recipe.duration * (1 - efficiencyBonus / 100))} min</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => handleCraftClick(recipe)}
                disabled={!canCraft || isProcessing || activeCrafting}
                className={`${styles.craftButton} ${isProcessing && isSelected ? styles.processing : ''}`}
              >
                {activeCrafting ? 'Furnace Busy' : (isProcessing && isSelected ? 'Processing...' : 'Start Crafting')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FurnaceView; 